import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Brain } from "lucide-react";
import { Reveal } from "@/components/Section";

// Concept mockups for WearSpray's Personal Skin Intelligence research program.
// These are illustrative, in-development ideas, not descriptions of the current app.
const CONSOLE_ITEMS = [
  {
    key: "uv-exposure",
    num: "01",
    title: "Your UV Exposure",
    video: "/skin-intel/uv-exposure.mp4",
    poster: "/skin-intel/uv-exposure-poster.jpg",
    description: "Real-time UV index, cloud cover and time outdoors, turned into one clear read: how much sun you've actually accumulated today.",
  },
  {
    key: "moisture-barrier",
    num: "02",
    title: "Your Moisture Barrier",
    video: "/skin-intel/moisture-barrier.mp4",
    poster: "/skin-intel/moisture-barrier-poster.jpg",
    description: "Hydration and moisture loss, tracked side by side, so you know the moment your skin's barrier needs backup.",
  },
  {
    key: "skin-response",
    num: "03",
    title: "Your Skin Response",
    video: "/skin-intel/skin-response.mp4",
    poster: "/skin-intel/skin-response-poster.jpg",
    description: "Redness tracked against your own calm baseline, not a generic chart, so a change actually means something.",
  },
  {
    key: "skin-environment",
    num: "04",
    title: "Your Skin Environment",
    video: "/skin-intel/skin-environment.mp4",
    poster: "/skin-intel/skin-environment-poster.jpg",
    description: "UV, heat, humidity and activity, read together, because what skin needs shifts with all four at once, not one number in isolation.",
  },
  {
    key: "application-coverage",
    num: "05",
    title: "Your Application Coverage",
    video: "/skin-intel/application-coverage.mp4",
    poster: "/skin-intel/application-coverage-poster.jpg",
    description: "The zones people miss most, like ears, nose and hairline, flagged before they become the sunburn you didn't see coming.",
  },
  {
    key: "routine-learned",
    num: "06",
    title: "Your Routine, Learned",
    video: "/skin-intel/routine-learned.mp4",
    poster: "/skin-intel/routine-learned-poster.jpg",
    description: "The longer it runs, the more precisely it predicts your next best moment to reapply. This is the model learning you.",
  },
  {
    key: "application-consistency",
    num: "07",
    title: "Your Application Consistency",
    video: "/skin-intel/application-consistency.mp4",
    poster: "/skin-intel/application-consistency-poster.jpg",
    description: "Reminders become a visible streak. Protection turns into a habit you can actually see yourself building.",
  },
  {
    key: "product-response",
    num: "08",
    title: "Your Product Response",
    video: "/skin-intel/product-response.mp4",
    poster: "/skin-intel/product-response-poster.jpg",
    description: "Hydration, comfort and redness, charted week over week: a record of what consistent use is actually doing for your skin.",
  },
  {
    key: "personal-baseline",
    num: "09",
    title: "Your Personal Baseline",
    video: "/skin-intel/personal-baseline.mp4",
    poster: "/skin-intel/personal-baseline-poster.jpg",
    description: "Thirty days of your own data becomes your normal, so every future reading is compared with you, not a stranger's average.",
  },
  {
    key: "application-dose",
    num: "10",
    title: "Your Application Dose",
    video: "/skin-intel/application-dose.mp4",
    poster: "/skin-intel/application-dose-poster.jpg",
    description: "Not just whether you applied, but how much and how evenly: the difference between partial and real protection.",
  },
];

function ConsoleItem({ item, isOpen, onToggle }) {
  return (
    <div className="border-b border-zinc-800" data-testid={`skin-console-item-${item.key}`}>
      <button
        onClick={onToggle}
        data-testid={`skin-console-title-${item.key}`}
        aria-expanded={isOpen}
        aria-controls={`skin-console-desc-${item.key}`}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
      >
        <span className={`font-heading text-base md:text-lg font-light tracking-tight transition-colors ${isOpen ? "text-accent" : "text-white group-hover:text-zinc-300"}`}>
          <span className="font-mono text-xs text-zinc-500 mr-3">{item.num}</span>
          {item.title}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-zinc-500 transition-transform duration-300 ${isOpen ? "rotate-180 text-accent" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`skin-console-desc-${item.key}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="font-body text-zinc-400 text-sm leading-relaxed pb-6 pr-8" data-testid={`skin-console-desc-${item.key}`}>
              {item.description}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function SkinConsole() {
  const [active, setActive] = useState(0);
  const current = CONSOLE_ITEMS[active];

  return (
    <section className="border-t border-zinc-800 bg-black overflow-hidden" data-testid="skin-console-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">
            Personal Skin Intelligence · Concept Preview
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
            The Console. What the AI consultant{" "}
            <span className="text-accent">could show you.</span>
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
            These are early mockups, in active development, imagining a
            personal skin consultation on your phone or a private web login:
            ten ways the Sense → Understand → Learn loop could become
            something you actually see. Click a title for the idea, watch the
            mockup on the right for how it could look and feel.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-16">
          <Reveal className="lg:col-span-5" delay={0.05}>
            <div data-testid="skin-console-list">
              {CONSOLE_ITEMS.map((item, i) => (
                <ConsoleItem key={item.key} item={item} isOpen={active === i} onToggle={() => setActive(i)} />
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="flex items-center gap-2 mb-3">
              <Brain size={12} className="text-accent" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">Mockup · In Development</span>
            </div>
            <div
              className="relative border border-zinc-800 bg-[#050505] aspect-video overflow-hidden"
              data-testid="skin-console-viewer"
            >
              <video
                key={current.key}
                data-testid="skin-console-video"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={current.poster}
                src={current.video}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">On Phone or Web</span>
              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500" data-testid="skin-console-index">
                {String(active + 1).padStart(2, "0")} / {String(CONSOLE_ITEMS.length).padStart(2, "0")}
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <p className="font-body text-xs text-zinc-500 mt-10 max-w-3xl leading-relaxed" data-testid="skin-console-disclaimer">
            These are trial mockups illustrating a future consultation-data
            experience, not a description of the current app. Personal Skin
            Intelligence is in active research and development.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
